<template>
  <div class="device-management">

    <!-- 设备统计 -->
    <div class="device-stats">
      <el-row :gutter="20">
        <el-col :span="6">
          <el-card class="stat-card" @click="filterByStatus('')">
            <div class="stat-item">
              <div class="stat-value">{{ deviceStats.total }}</div>
              <div class="stat-label">设备总数</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card" :class="{ active: selectedStatus === 1 }" @click="filterByStatus(1)">
            <div class="stat-item">
              <div class="stat-value online">{{ deviceStats.online }}</div>
              <div class="stat-label">在线设备</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card" :class="{ active: selectedStatus === 0 }" @click="filterByStatus(0)">
            <div class="stat-item">
              <div class="stat-value offline">{{ deviceStats.offline }}</div>
              <div class="stat-label">离线设备</div>
            </div>
          </el-card>
        </el-col>
        <el-col :span="6">
          <el-card class="stat-card">
            <div class="stat-item">
              <div class="stat-value rate">{{ deviceStats.onlineRate }}%</div>
              <div class="stat-label">在线率</div>
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>

    <!-- 设备列表 -->
    <el-card class="device-list">
      <template #header>
        <div class="card-header">
          <span>设备列表</span>
          <div class="header-actions">
            <el-select v-model="selectedDeviceType" placeholder="设备类型" @change="loadDevices" style="width: 150px;">
              <el-option label="全部设备" value=""></el-option>
              <el-option
                v-for="type in deviceTypes"
                :key="type.code"
                :label="type.name"
                :value="type.code">
              </el-option>
            </el-select>
            <el-select v-model="selectedStatus" placeholder="设备状态" @change="filterDevices" style="width: 120px;">
              <el-option label="全部状态" value=""></el-option>
              <el-option label="在线" :value="1"></el-option>
              <el-option label="离线" :value="0"></el-option>
            </el-select>
            <el-button @click="showImportDialog = true">
              <el-icon><Upload /></el-icon>
              导入设备
            </el-button>
            <el-button type="primary" @click="showAddDialog = true">
              <el-icon><Plus /></el-icon>
              添加设备
            </el-button>
            <el-button @click="refreshDevices">
              <el-icon><Refresh /></el-icon>
              刷新
            </el-button>
          </div>
        </div>
      </template>

      <!-- 列表视图 -->
      <div class="table-container">
        <el-table 
          :data="devices" 
          v-loading="loading"
          style="width: 100%"
          table-layout="auto"
          :fit="true"
          @sort-change="handleSortChange"
        >
          <el-table-column prop="deviceName" label="设备名称" min-width="120" header-align="center" align="center" show-overflow-tooltip></el-table-column>
          <el-table-column prop="deviceId" label="设备ID" min-width="140" header-align="center" align="center" show-overflow-tooltip sortable="custom"></el-table-column>
          <el-table-column label="产品信息" min-width="180" header-align="center" align="center">
            <template #default="scope">
              <div v-if="scope.row.productName" class="product-info">
                <div class="product-name">{{ scope.row.productName }}</div>
                <div class="product-model">{{ scope.row.productModel }}</div>
              </div>
              <el-tag v-else size="small" type="info">未关联产品</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="设备类型" min-width="100" header-align="center" align="center">
            <template #default="scope">
              <el-tag size="small" effect="plain">
                {{ scope.row.deviceTypeName || '未知' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="状态" min-width="80" header-align="center" align="center">
            <template #default="scope">
              <el-tag
                :type="getStatusType(scope.row.status)"
                size="small">
                {{ getStatusText(scope.row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="归属家庭" min-width="120" header-align="center" align="center">
            <template #default="scope">
              <el-tag v-if="scope.row.familyName" size="small" type="primary">
                {{ scope.row.familyName }}
              </el-tag>
              <el-tag v-else size="small" type="info">未分配</el-tag>
            </template>
          </el-table-column>
          <el-table-column label="位置" min-width="90" header-align="center" align="center" show-overflow-tooltip>
            <template #default="scope">
              {{ getLocationName(scope.row.room) }}
            </template>
          </el-table-column>
          <el-table-column label="通知策略" header-align="center" align="center" min-width="150">
            <template #default="scope">
              <el-select 
                v-model="scope.row.notificationPolicyId" 
                size="small" 
                placeholder="选择策略"
                @change="updateDeviceNotificationPolicy(scope.row)"
                style="width: 100%">
                <el-option value="" label="使用全局策略" />
                <el-option 
                  v-for="policy in enabledNotificationPolicies" 
                  :key="policy.id" 
                  :value="policy.id"
                  :label="policy.name">
                </el-option>
              </el-select>
            </template>
          </el-table-column>
          <el-table-column label="最后心跳" min-width="110" header-align="center" align="center" show-overflow-tooltip>
            <template #default="scope">
              <span class="heartbeat-time">{{ formatTime(scope.row.lastHeartbeat) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="操作" min-width="320" header-align="center" align="center" fixed="right">
            <template #default="scope">
              <div class="action-buttons">
                <el-button
                  size="small"
                  type="primary"
                  plain
                  @click="$router.push(`/devices/${scope.row.id}`)">
                  详情
                </el-button>
                <el-button 
                  size="small" 
                  type="warning"
                  plain
                  @click="editDevice(scope.row)">
                  修改
                </el-button>
                <el-button 
                  size="small" 
                  :type="scope.row.armStatus === 'armed' ? 'warning' : 'success'"
                  plain
                  @click="toggleDeviceArmStatus(scope.row)">
                  {{ scope.row.armStatus === 'armed' ? '撤防' : '布防' }}
                </el-button>
                <el-button 
                  size="small" 
                  type="danger" 
                  plain
                  @click="deleteDevice(scope.row)">
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
    </el-card>

    <!-- 设备详情对话框 -->
    <el-dialog 
      v-model="detailDialogVisible" 
      title="设备详情" 
      width="600px">
      <div v-if="selectedDevice" class="device-detail">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="设备名称">
            {{ selectedDevice.deviceName }}
          </el-descriptions-item>
          <el-descriptions-item label="设备ID">
            {{ selectedDevice.deviceId }}
          </el-descriptions-item>
          <el-descriptions-item label="设备类型">
            {{ selectedDevice.deviceTypeName || '未知' }}
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusType(selectedDevice.status)">
              {{ getStatusText(selectedDevice.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="产品信息" :span="2" v-if="selectedDevice.productName">
            {{ selectedDevice.productName }} ({{ selectedDevice.productModel }})
          </el-descriptions-item>
          <el-descriptions-item label="归属家庭" v-if="selectedDevice.familyName">
            {{ selectedDevice.familyName }}
          </el-descriptions-item>
          <el-descriptions-item label="归属家庭" v-else>
            <el-tag size="small" type="info">未分配</el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="MAC地址">
            {{ selectedDevice.macAddress || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="IP地址">
            {{ selectedDevice.ipAddress || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="位置">
            {{ getLocationName(selectedDevice.room) }}
          </el-descriptions-item>
          <el-descriptions-item label="固件版本">
            {{ selectedDevice.firmwareVersion || '-' }}
          </el-descriptions-item>
          <el-descriptions-item label="最后心跳">
            {{ formatTime(selectedDevice.lastHeartbeat) }}
          </el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>

    <!-- 添加设备对话框 -->
    <el-dialog v-model="showAddDialog" title="添加设备" width="600px" @close="resetAddForm">
      <el-form :model="addForm" :rules="addRules" ref="addFormRef" label-width="100px">
        <el-form-item label="设备编号" prop="deviceCode">
          <el-input v-model="addForm.deviceCode" placeholder="请输入设备编号(IMEI/SN)" maxlength="20"></el-input>
          <div class="form-tip">设备编号通常在设备标签或包装盒上，如IMEI号或序列号</div>
        </el-form-item>
        <el-form-item label="设备名称" prop="deviceName">
          <el-input v-model="addForm.deviceName" placeholder="请输入设备名称" maxlength="50"></el-input>
        </el-form-item>
        <el-form-item label="产品型号" prop="productId">
          <el-select 
            v-model="addForm.productId" 
            placeholder="请选择产品型号" 
            style="width: 100%" 
            filterable 
            :loading="loadingProducts"
            @change="onProductChange"
          >
            <el-option 
              v-for="product in allAvailableProducts" 
              :key="product.id" 
              :label="`${product.name} (${product.model})`" 
              :value="product.id"
            >
              <div style="display: flex; justify-content: space-between;">
                <span>{{ product.name }}</span>
                <span style="color: #8492a6; font-size: 12px;">{{ product.model }}</span>
              </div>
            </el-option>
          </el-select>
          <div class="form-tip">选择具体的产品型号，产品类型和设备类型将自动填充</div>
        </el-form-item>
        <el-form-item label="产品类型">
          <el-input v-model="categoryDisplay" placeholder="选择产品型号后自动填充" disabled></el-input>
        </el-form-item>
        <el-form-item label="设备类型">
          <el-input v-model="deviceTypeDisplay" placeholder="选择产品型号后自动填充" disabled></el-input>
        </el-form-item>
        <el-form-item label="安装位置" prop="location">
          <el-select 
            v-model="addForm.location" 
            placeholder="请选择安装位置" 
            style="width: 100%"
            filterable>
            <el-option 
              v-for="location in familyLocations" 
              :key="location.id" 
              :label="location.name" 
              :value="location.id">
            </el-option>
          </el-select>
          <div class="form-tip">请选择场所，如没有合适的场所请先在场所管理中添加</div>
        </el-form-item>
        <el-form-item label="通知策略" prop="notificationPolicy">
          <el-select 
            v-model="addForm.notificationPolicy" 
            placeholder="请选择通知策略" 
            style="width: 100%">
            <el-option 
              v-for="policy in notificationPolicies" 
              :key="policy.id" 
              :label="policy.name" 
              :value="policy.id">
            </el-option>
          </el-select>
          <div class="form-tip">选择设备触发告警时的通知方式</div>
        </el-form-item>
        <el-form-item label="设备描述">
          <el-input 
            v-model="addForm.description" 
            type="textarea" 
            :rows="3" 
            placeholder="设备描述信息（可选）" 
            maxlength="200"
          ></el-input>
        </el-form-item>
        <el-form-item label="MAC地址">
          <el-input v-model="addForm.macAddress" placeholder="MAC地址（可选）" maxlength="17"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showAddDialog = false">取消</el-button>
          <el-button type="primary" @click="submitAddDevice" :loading="addingDevice">添加设备</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- 编辑设备对话框 -->
    <el-dialog v-model="editDeviceVisible" title="编辑设备" width="600px" @close="resetEditDeviceForm">
      <el-form :model="editDeviceForm" :rules="editDeviceRules" ref="editDeviceFormRef" label-width="100px">
        <el-form-item label="设备ID" prop="deviceId">
          <el-input v-model="editDeviceForm.deviceId" placeholder="请输入设备ID" maxlength="50"></el-input>
          <div class="form-tip">设备的唯一标识符，修改后请确保不与其他设备重复</div>
        </el-form-item>
        <el-form-item label="设备名称" prop="deviceName">
          <el-input v-model="editDeviceForm.deviceName" placeholder="请输入设备名称" maxlength="50"></el-input>
        </el-form-item>
        <el-form-item label="产品型号" prop="productId">
          <el-select 
            v-model="editDeviceForm.productId" 
            placeholder="请选择产品型号" 
            style="width: 100%" 
            filterable 
            :loading="loadingProducts"
            @change="onEditProductChange"
          >
            <el-option 
              v-for="product in allAvailableProducts" 
              :key="product.id" 
              :label="`${product.name} (${product.model})`" 
              :value="product.id"
            >
              <div style="display: flex; justify-content: space-between;">
                <span>{{ product.name }}</span>
                <span style="color: #8492a6; font-size: 12px;">{{ product.model }}</span>
              </div>
            </el-option>
          </el-select>
          <div class="form-tip">选择具体的产品型号，产品类型和设备类型将自动填充</div>
        </el-form-item>
        <el-form-item label="产品类型">
          <el-input v-model="editCategoryDisplay" placeholder="选择产品型号后自动填充" disabled></el-input>
        </el-form-item>
        <el-form-item label="设备类型">
          <el-input v-model="editDeviceTypeDisplay" placeholder="选择产品型号后自动填充" disabled></el-input>
        </el-form-item>
        <el-form-item label="安装位置" prop="location">
          <el-select 
            v-model="editDeviceForm.location" 
            placeholder="请选择安装位置" 
            style="width: 100%"
            filterable>
            <el-option 
              v-for="location in familyLocations" 
              :key="location.id" 
              :label="location.name" 
              :value="location.id">
            </el-option>
          </el-select>
          <div class="form-tip">请选择场所，如没有合适的场所请先在场所管理中添加</div>
        </el-form-item>
        <el-form-item label="设备描述">
          <el-input 
            v-model="editDeviceForm.description" 
            type="textarea" 
            :rows="3" 
            placeholder="设备描述信息（可选）" 
            maxlength="200"
          ></el-input>
        </el-form-item>
        <el-form-item label="MAC地址">
          <el-input v-model="editDeviceForm.macAddress" placeholder="MAC地址（可选）" maxlength="17"></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="editDeviceVisible = false">取消</el-button>
          <el-button type="primary" @click="submitEditDevice" :loading="editingDevice">保存修改</el-button>
        </div>
      </template>
    </el-dialog>

    <!-- CSV导入设备对话框 -->
    <el-dialog v-model="showImportDialog" title="导入设备" width="700px" @close="resetImportForm">
      <div class="import-section">
        <el-alert
          title="导入说明"
          type="info"
          :closable="false"
          style="margin-bottom: 20px;">
          <template #default>
            <div class="import-instructions">
              <p><strong>CSV文件格式要求：</strong></p>
              <ul>
                <li>第一行必须是表头：deviceCode,deviceName,productModel,familyName,location,description,macAddress</li>
                <li>deviceCode（设备编号）和deviceName（设备名称）为必填字段</li>
                <li>productModel（产品型号）必须是系统中已存在的产品型号</li>
                <li>familyName（家庭名称）必须是已存在的家庭名称</li>
                <li>文件编码必须为UTF-8格式</li>
              </ul>
            </div>
          </template>
        </el-alert>

        <div class="import-actions">
          <el-button @click="downloadTemplate">
            <el-icon><Download /></el-icon>
            下载模板
          </el-button>
          <el-upload
            ref="uploadRef"
            :auto-upload="false"
            :show-file-list="false"
            accept=".csv"
            :before-upload="beforeUpload"
            style="display: inline-block; margin-left: 10px;"
          >
            <el-button type="primary">
              <el-icon><Upload /></el-icon>
              选择CSV文件
            </el-button>
          </el-upload>
        </div>

        <div v-if="importFile" class="file-info">
          <el-card style="margin-top: 20px;">
            <template #header>
              <div class="file-header">
                <span>已选择文件：{{ importFile.name }}</span>
                <el-button link @click="removeFile">移除</el-button>
              </div>
            </template>
            <div class="file-details">
              <p>文件大小：{{ formatFileSize(importFile.size) }}</p>
              <p>修改时间：{{ formatDateTime(importFile.lastModified) }}</p>
            </div>
          </el-card>
        </div>

        <div v-if="previewData.length > 0" class="preview-section">
          <h4>数据预览（前5行）</h4>
          <el-table :data="previewData.slice(0, 5)" border size="small">
            <el-table-column prop="deviceCode" label="设备编号" width="120"></el-table-column>
            <el-table-column prop="deviceName" label="设备名称" width="120"></el-table-column>
            <el-table-column prop="productModel" label="产品型号" width="120"></el-table-column>
            <el-table-column prop="familyName" label="家庭名称" width="100"></el-table-column>
            <el-table-column prop="location" label="位置" width="80"></el-table-column>
            <el-table-column prop="description" label="描述" min-width="100"></el-table-column>
          </el-table>
          <p class="preview-summary">共 {{ previewData.length }} 条数据待导入</p>
        </div>

        <div v-if="importErrors.length > 0" class="error-section">
          <h4>数据验证错误</h4>
          <el-table :data="importErrors" border size="small" max-height="200">
            <el-table-column prop="row" label="行号" width="60"></el-table-column>
            <el-table-column prop="field" label="字段" width="100"></el-table-column>
            <el-table-column prop="error" label="错误信息" min-width="200"></el-table-column>
          </el-table>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <el-button @click="showImportDialog = false">取消</el-button>
          <el-button 
            type="primary" 
            @click="submitImport" 
            :loading="importing"
            :disabled="!importFile || importErrors.length > 0 || previewData.length === 0"
          >
            导入设备
          </el-button>
        </div>
      </template>
    </el-dialog>

  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus, Upload, Refresh, Download } from '@element-plus/icons-vue'
import { deviceApi, productApi, familyApi, familyLocationApi, notificationPolicyApi, deviceTypeApi } from '@/utils/api'

export default {
  name: 'DeviceManagement',
  components: {
    Plus, Upload, Refresh, Download
  },
  setup() {
    const loading = ref(false)
    const selectedDeviceType = ref('')
    const selectedStatus = ref('') // 选中的设备状态筛选
    const devices = ref([])
    const allDevices = ref([]) // 存储所有设备数据，用于前端筛选
    // 排序状态
    const sortState = ref({ prop: '', order: '' })
    const deviceTypes = ref([])
    const deviceStats = ref({
      total: 0,
      online: 0,
      offline: 0,
      onlineRate: 0
    })
    
    const detailDialogVisible = ref(false)
    const selectedDevice = ref(null)
    
    // 编辑设备相关
    const editDeviceVisible = ref(false)
    const editingDevice = ref(false)
    const editDeviceForm = ref({
      id: null,
      deviceId: null,
      deviceName: '',
      productId: null,
      category: '',
      deviceType: '',
      deviceTypeId: null,
      deviceTypeObject: null,
      location: '',
      description: '',
      macAddress: ''
    })
    
    // 编辑设备时的产品显示字段
    const editCategoryDisplay = ref('')
    const editDeviceTypeDisplay = ref('')
    
    // 位置管理相关
    const familyLocations = ref([]) // 存储家庭位置列表
    const locationMap = ref({}) // 位置ID到名称的映射
    
    // 添加设备相关
    const showAddDialog = ref(false)
    const addingDevice = ref(false)
    const addForm = ref({
      deviceCode: '',
      deviceName: '',
      category: '',
      deviceType: '',
      productId: null,
      familyId: null,
      location: '',
      notificationPolicy: null,
      description: '',
      macAddress: ''
    })
    
    // 产品管理相关
    const availableCategories = ref([
      { label: '安防设备', value: 'security' },
      { label: '环境监测', value: 'environment' },
      { label: '智能家居', value: 'smarthome' },
      { label: '健康监测', value: 'health' }
    ])

    // 通知策略选项（从数据库加载）
    const notificationPolicies = ref([])
    
    // 已启用的通知策略
    const enabledNotificationPolicies = computed(() => {
      return notificationPolicies.value.filter(policy => policy.enabled)
    })
    const availableProducts = ref([])
    const allAvailableProducts = ref([])
    const availableDeviceTypes = ref([])
    const availableFamilies = ref([])
    const loadingProducts = ref(false)
    const categoryDisplay = ref('')
    const deviceTypeDisplay = ref('')
    
    // 表单验证规则
    const addRules = ref({
      deviceCode: [
        { required: true, message: '请输入设备编号', trigger: 'blur' },
        { min: 3, max: 20, message: '设备编号长度在 3 到 20 个字符', trigger: 'blur' }
      ],
      deviceName: [
        { required: true, message: '请输入设备名称', trigger: 'blur' },
        { min: 2, max: 50, message: '设备名称长度在 2 到 50 个字符', trigger: 'blur' }
      ],
      productId: [
        { required: true, message: '请选择产品型号', trigger: 'change' }
      ],
      location: [
        { required: true, message: '请选择安装位置', trigger: 'change' }
      ],
      notificationPolicy: [
        { required: true, message: '请选择通知策略', trigger: 'change' }
      ],
    })
    
    // 编辑设备表单验证规则
    const editDeviceRules = ref({
      deviceId: [
        { required: true, message: '请输入设备ID', trigger: 'blur' },
        { min: 1, max: 50, message: '设备ID长度在 1 到 50 个字符', trigger: 'blur' },
        { pattern: /^[a-zA-Z0-9_-]+$/, message: '设备ID只能包含字母、数字、下划线和横线', trigger: 'blur' }
      ],
      deviceName: [
        { required: true, message: '请输入设备名称', trigger: 'blur' },
        { min: 2, max: 50, message: '设备名称长度在 2 到 50 个字符', trigger: 'blur' }
      ],
      productId: [
        { required: true, message: '请选择产品型号', trigger: 'change' }
      ]
    })
    
    // 导入设备相关
    const showImportDialog = ref(false)
    const importing = ref(false)
    const importFile = ref(null)
    const previewData = ref([])
    const importErrors = ref([])
    
    // 表单引用
    const addFormRef = ref()
    const editDeviceFormRef = ref()
    
    let cleanupFunctions = []
    
    onMounted(() => {
      loadDeviceTypes()
      loadFamilyLocations() // 先加载位置信息
      loadNotificationPolicies() // 加载通知策略
      loadDevices()
      loadFamilies()
      loadAllProducts()
    })
    
    onBeforeUnmount(() => {
      // 清理所有引用
      cleanupFunctions.forEach(cleanup => cleanup())
      cleanupFunctions = []
      
      // 确保对话框关闭
      detailDialogVisible.value = false
      selectedDevice.value = null
    })
    
    const loadDeviceTypes = async () => {
      try {
        const response = await deviceTypeApi.getDeviceTypes()
        if (response.success && response.data) {
          deviceTypes.value = response.data
          console.log('加载设备类型成功:', response.data.length, '个类型')
        } else {
          console.error('获取设备类型失败:', response.message)
          ElMessage.error('加载设备类型失败，请刷新页面重试')
          deviceTypes.value = []
        }
      } catch (error) {
        console.error('加载设备类型失败:', error)
        ElMessage.error('加载设备类型失败，请检查网络连接并刷新页面重试')
        deviceTypes.value = []
      }
    }
    
    const loadDevices = async () => {
      loading.value = true
      try {
        // 获取当前用户的家庭ID
        const family = localStorage.getItem('family')
        if (!family || family === 'null') {
          ElMessage.warning('暂未关联家庭，无法加载设备列表')
          devices.value = []
          loading.value = false
          return
        }
        
        const familyData = JSON.parse(family)
        if (!familyData || !familyData.id) {
          ElMessage.warning('家庭信息无效，无法加载设备列表')
          devices.value = []
          loading.value = false
          return
        }
        const familyId = familyData.id
        
        const params = {}
        if (selectedDeviceType.value) {
          params.deviceType = selectedDeviceType.value
        }
        
        // 使用家庭设备API获取当前用户家庭的设备
        const response = await deviceApi.getFamilyDevices(familyId, params)
        if (response.data) {
          allDevices.value = response.data // 保存所有设备
          devices.value = response.data
          console.log('设备加载成功:', devices.value.length, '台设备')
          // 检查设备的通知策略字段
          if (devices.value.length > 0) {
            console.log('第一台设备的策略信息:', devices.value[0].notificationPolicyId, devices.value[0].deviceName)
          }
          // 应用状态筛选
          filterDevices()
          // 应用当前排序
          applySort()
          // 计算统计信息
          calculateStats()
        }
      } catch (error) {
        console.error('加载设备列表失败:', error)
        ElMessage.error('加载设备列表失败')
        // 清空设备列表
        devices.value = []
        calculateStats()
      } finally {
        loading.value = false
      }
    }
    
    const loadFamilies = async () => {
      try {
        const response = await familyApi.getFamilies()
        if (response.data) {
          availableFamilies.value = response.data
        } else {
          console.error('获取家庭列表失败:', response.message)
          ElMessage.error('加载家庭列表失败，请刷新页面重试')
          availableFamilies.value = []
        }
      } catch (error) {
        console.error('加载家庭列表失败:', error)
        ElMessage.error('加载家庭列表失败，请检查网络连接并刷新页面重试')
        availableFamilies.value = []
      }
    }
    
    // 加载家庭位置信息
    const loadFamilyLocations = async () => {
      try {
        const family = localStorage.getItem('family')
        if (!family || family === 'null') {
          console.warn('未找到家庭信息，无法加载位置')
          return
        }
        
        const familyData = JSON.parse(family)
        if (!familyData || !familyData.id) {
          console.warn('家庭信息无效，无法加载位置')
          return
        }
        const familyId = familyData.id
        
        const response = await familyLocationApi.getFamilyLocations(familyId)
        if (response && response.success && response.data) {
          familyLocations.value = response.data
          // 创建位置ID到名称的映射
          const map = {}
          response.data.forEach(location => {
            map[location.id] = location.name
          })
          locationMap.value = map
          console.log('加载家庭位置成功:', response.data.length, '个位置')
        }
      } catch (error) {
        console.error('加载家庭位置失败:', error)
        ElMessage.error('加载家庭位置失败，请刷新页面重试')
        familyLocations.value = []
        locationMap.value = {}
      }
    }
    
    // 加载家庭通知策略
    const loadNotificationPolicies = async () => {
      try {
        const family = localStorage.getItem('family')
        if (!family || family === 'null') {
          console.warn('未找到家庭信息，无法加载通知策略')
          return
        }
        
        const familyData = JSON.parse(family)
        if (!familyData || !familyData.id) {
          console.warn('家庭信息无效，无法加载通知策略')
          return
        }
        const familyId = familyData.id
        
        // 调用API获取通知策略
        const response = await notificationPolicyApi.getNotificationPoliciesByFamilyId(familyId)
        if (response.success && response.data) {
          notificationPolicies.value = response.data.map(policy => ({
            id: policy.id,
            name: policy.name,
            description: policy.description,
            enabled: policy.enabled !== false  // 确保enabled字段存在
          }))
          
          // 如果当前表单没有选择通知策略，则设置第一个为默认值
          if (!addForm.value.notificationPolicy && notificationPolicies.value.length > 0) {
            addForm.value.notificationPolicy = notificationPolicies.value[0].id
          }
          
          console.log('加载通知策略成功:', response.data.length, '个策略', notificationPolicies.value)
        } else {
          console.error('获取通知策略失败:', response.message)
          ElMessage.error('加载通知策略失败，请刷新页面重试')
          notificationPolicies.value = []
        }
      } catch (error) {
        console.error('加载通知策略失败:', error)
        ElMessage.error('加载通知策略失败，请检查网络连接并刷新页面重试')
        notificationPolicies.value = []
      }
    }
    
    // 根据位置ID获取位置名称
    const getLocationName = (locationId) => {
      if (!locationId) return '未设置'
      return locationMap.value[locationId] || locationId // 如果找不到映射，返回原ID
    }
    
    const calculateStats = () => {
      // 使用allDevices计算全局统计信息
      const allDevicesList = allDevices.value.length > 0 ? allDevices.value : devices.value
      const total = allDevicesList.length
      const online = allDevicesList.filter(d => {
        const deviceStatus = Number(d.status)
        return deviceStatus === 1
      }).length
      const offline = allDevicesList.filter(d => {
        const deviceStatus = Number(d.status)
        return deviceStatus === 0
      }).length
      const onlineRate = total > 0 ? Math.round((online / total) * 100) : 0

      deviceStats.value = { total, online, offline, onlineRate }
      console.log('统计信息:', { total, online, offline, onlineRate })
    }
    
    const refreshDevices = () => {
      loadDevices()
    }

    // 按状态筛选设备
    const filterDevices = () => {
      if (selectedStatus.value === '') {
        // 显示所有设备
        devices.value = [...allDevices.value]
      } else {
        // 按状态筛选，确保类型转换
        devices.value = allDevices.value.filter(d => {
          const deviceStatus = Number(d.status)
          console.log('筛选设备:', d.deviceName, 'status:', d.status, '转换后:', deviceStatus, '筛选条件:', selectedStatus.value, '匹配:', deviceStatus === selectedStatus.value)
          return deviceStatus === selectedStatus.value
        })
      }
      console.log('筛选后的设备数量:', devices.value.length)
      // 重新应用排序
      applySort()
      // 重新计算统计信息（基于筛选后的设备）
      calculateFilteredStats()
    }

    // 通过卡片点击筛选
    const filterByStatus = (status) => {
      selectedStatus.value = status
      filterDevices()
    }

    // 计算筛选后的统计信息
    const calculateFilteredStats = () => {
      const total = devices.value.length
      const online = devices.value.filter(d => {
        const deviceStatus = Number(d.status)
        return deviceStatus === 1
      }).length
      const offline = devices.value.filter(d => {
        const deviceStatus = Number(d.status)
        return deviceStatus === 0
      }).length
      const onlineRate = total > 0 ? Math.round((online / total) * 100) : 0

      console.log('筛选后统计:', { total, online, offline, onlineRate })
      // 注意：这里不更新deviceStats，因为那个是全局统计
      // 仅用于验证筛选是否正确
    }

    // 处理表头排序变更（Element Plus 自定义排序）
    const handleSortChange = ({ prop, order }) => {
      sortState.value = { prop, order }
      applySort()
    }

    // 应用当前排序到设备列表
    const applySort = () => {
      const { prop, order } = sortState.value
      if (!prop || !order) return
      const isAsc = order === 'ascending'
      const compare = (a, b) => {
        let va = a[prop]
        let vb = b[prop]
        if (va == null && vb == null) return 0
        if (va == null) return 1
        if (vb == null) return -1
        // 数字优先
        if (typeof va === 'number' && typeof vb === 'number') {
          return isAsc ? va - vb : vb - va
        }
        // 其他按字符串比较
        va = String(va)
        vb = String(vb)
        return isAsc ? va.localeCompare(vb) : vb.localeCompare(va)
      }
      devices.value = [...devices.value].sort(compare)
    }
    
    
    const viewDeviceDetail = (device) => {
      selectedDevice.value = device
      detailDialogVisible.value = true
    }
    
    // 编辑设备
    const editDevice = (device) => {
      console.log('编辑设备对象:', device)
      console.log('设备ID字段:', { id: device.id, deviceId: device.deviceId })
      console.log('设备类型信息:', { 
        deviceType: device.deviceType, 
        deviceTypeId: device.deviceTypeId,
        type: device.type 
      })
      
      // 直接使用位置ID，因为选择器现在使用ID作为value
      let locationId = device.room || ''
      
      // 查找对应的产品信息
      let productId = null
      let category = ''
      let deviceType = ''
      
      if (device.productName && device.productModel) {
        const foundProduct = allAvailableProducts.value.find(p => 
          p.name === device.productName && p.model === device.productModel
        )
        if (foundProduct) {
          productId = foundProduct.id
          category = foundProduct.category
          deviceType = foundProduct.deviceTypeId
        }
      }
      
      editDeviceForm.value = {
        id: device.id,
        deviceId: device.deviceId,
        deviceName: device.deviceName,
        productId: productId,
        category: category,
        deviceType: deviceType,
        deviceTypeId: device.deviceTypeId, // 保存设备类型ID
        deviceTypeObject: device.deviceType, // 保存原始设备类型对象
        location: locationId, // 使用位置ID
        description: device.description || '',
        macAddress: device.macAddress || ''
      }
      
      // 更新显示字段
      editCategoryDisplay.value = getCategoryText(category)
      editDeviceTypeDisplay.value = getDeviceTypeName(deviceType)
      
      editDeviceVisible.value = true
    }
    
    // 提交编辑设备
    const submitEditDevice = async () => {
      if (!editDeviceFormRef.value) {
        return
      }
      
      try {
        await editDeviceFormRef.value.validate()
        editingDevice.value = true
        
        const updateData = {
          id: editDeviceForm.value.id,
          deviceId: editDeviceForm.value.deviceId || editDeviceForm.value.id,
          deviceName: editDeviceForm.value.deviceName,
          productId: editDeviceForm.value.productId,
          deviceTypeId: editDeviceForm.value.deviceTypeId || editDeviceForm.value.deviceType,
          room: editDeviceForm.value.location, // location字段对应数据库的room字段，保存场所ID
          description: editDeviceForm.value.description,
          macAddress: editDeviceForm.value.macAddress
        }
        
        console.log('发送的更新数据:', updateData)
        
        const response = await deviceApi.updateDevice(editDeviceForm.value.deviceId, updateData)
        if (response && response.success) {
          ElMessage.success('设备修改成功')
          editDeviceVisible.value = false
          resetEditDeviceForm()
          await loadDevices() // 重新加载设备列表
        } else {
          ElMessage.error(response?.message || '设备修改失败')
        }
      } catch (error) {
        if (error !== false) { // 非表单验证错误
          console.error('设备修改失败:', error)
          ElMessage.error('设备修改失败')
        }
      } finally {
        editingDevice.value = false
      }
    }
    
    // 编辑设备时的产品选择变化
    const onEditProductChange = (productId) => {
      const selectedProduct = allAvailableProducts.value.find(p => p.id === productId)
      if (selectedProduct) {
        // 自动填充产品类型和设备类型
        editDeviceForm.value.category = selectedProduct.category
        editDeviceForm.value.deviceType = selectedProduct.deviceTypeId
        
        // 更新显示文本
        editCategoryDisplay.value = getCategoryText(selectedProduct.category)
        editDeviceTypeDisplay.value = getDeviceTypeName(selectedProduct.deviceTypeId)
      }
    }
    
    // 重置编辑设备表单
    const resetEditDeviceForm = () => {
      editDeviceForm.value = {
        id: null,
        deviceId: null,
        deviceName: '',
        productId: null,
        category: '',
        deviceType: '',
        deviceTypeId: null,
        deviceTypeObject: null,
        location: '',
        description: '',
        macAddress: ''
      }
      editCategoryDisplay.value = ''
      editDeviceTypeDisplay.value = ''
    }
    
    const toggleDeviceArmStatus = async (device) => {
      try {
        const newArmStatus = device.armStatus === 'armed' ? 'disarmed' : 'armed'
        const action = newArmStatus === 'armed' ? '布防' : '撤防'
        
        await ElMessageBox.confirm(`确定要${action}设备 "${device.deviceName}" 吗？`, '确认操作')
        
        // 调用API更新设备布防状态
        await deviceApi.toggleDeviceArm(device.deviceId, newArmStatus)
        
        device.armStatus = newArmStatus
        ElMessage.success(`设备${action}成功`)
      } catch (error) {
        if (error !== 'cancel') {
          console.error('切换设备布防状态失败:', error)
          ElMessage.error('操作失败')
        }
      }
    }
    
    const deleteDevice = async (device) => {
      try {
        await ElMessageBox.confirm(`确定要删除设备 "${device.deviceName}" 吗？此操作不可恢复！`, '确认删除', {
          type: 'warning'
        })
        
        // 调用API删除设备
        await deviceApi.deleteDevice(device.deviceId)
        
        const index = devices.value.findIndex(d => d.id === device.id)
        if (index > -1) {
          devices.value.splice(index, 1)
          calculateStats()
          ElMessage.success('设备删除成功')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除设备失败:', error)
          ElMessage.error('删除失败')
        }
      }
    }
    
    // 更新设备通知策略
    const updateDeviceNotificationPolicy = async (device) => {
      try {
        // 调用API更新设备的通知策略
        const response = await deviceApi.updateDeviceNotificationPolicy(device.deviceId, device.notificationPolicyId)
        
        if (response && response.success) {
          ElMessage.success(`已为设备 ${device.deviceName} 更新通知策略`)
          
          // 重新加载设备列表以确保数据一致性
          await loadDevices()
        } else {
          throw new Error(response?.message || '更新失败')
        }
      } catch (error) {
        console.error('更新设备策略失败:', error)
        ElMessage.error('更新通知策略失败，请重试')
        // 重新加载设备列表以回滚UI状态
        await loadDevices()
      }
    }
    
    const formatTime = (timeStr) => {
      if (!timeStr) return '-'
      const date = new Date(timeStr)
      return date.toLocaleString('zh-CN')
    }
    
    
    // 设备类型映射
    const deviceTypeMapping = {
      security: [
        { label: '烟感器', value: 'smoke' },
        { label: '燃气传感器', value: 'gas' },
        { label: '门窗传感器', value: 'door' },
        { label: '人体感应器', value: 'pir' },
        { label: '摄像头', value: 'camera' }
      ],
      environment: [
        { label: '温湿度传感器', value: 'temp_humidity' },
        { label: '空气质量传感器', value: 'air_quality' },
        { label: '水浸传感器', value: 'water' }
      ],
      smarthome: [
        { label: '智能开关', value: 'switch' },
        { label: '智能插座', value: 'socket' },
        { label: '门磁传感器', value: 'door' }
      ],
      health: [
        { label: '健康手环', value: 'wearable' },
        { label: '定位器', value: 'tracker' }
      ]
    }
    
    
    // 产品类型变化（保留函数以避免错误，但不再使用）
    const onCategoryChange = (category) => {
      // 这个函数现在不再需要，因为类型是通过产品选择自动填充的
    }
    
    // 设备类型变化（保留函数以避免错误，但不再使用）
    const onDeviceTypeChange = (deviceType) => {
      // 这个函数现在不再需要，因为类型是通过产品选择自动填充的
    }
    
    // 加载所有产品
    const loadAllProducts = async () => {
      try {
        loadingProducts.value = true
        const response = await productApi.getProducts({})
        if (response && response.success) {
          allAvailableProducts.value = response.data || []
        } else {
          console.error('获取产品列表失败:', response?.message)
          ElMessage.error('加载产品列表失败，请刷新页面重试')
          allAvailableProducts.value = []
        }
      } catch (error) {
        console.error('加载产品失败:', error)
        ElMessage.error('加载产品列表失败，请检查网络连接并刷新页面重试')
        allAvailableProducts.value = []
      } finally {
        loadingProducts.value = false
      }
    }
    
    
    // 获取产品类型文本
    const getCategoryText = (category) => {
      const textMap = {
        'security': '安防设备',
        'environment': '环境监测',
        'smarthome': '智能家居',
        'health': '健康监测'
      }
      return textMap[category] || '未知'
    }
    
    
    // 产品选择变化
    const onProductChange = (productId) => {
      const selectedProduct = allAvailableProducts.value.find(p => p.id === productId)
      if (selectedProduct) {
        // 自动填充设备名称（如果为空）
        if (!addForm.value.deviceName) {
          addForm.value.deviceName = selectedProduct.name
        }
        
        // 自动填充产品类型和设备类型
        addForm.value.category = selectedProduct.category
        addForm.value.deviceType = selectedProduct.deviceTypeId
        
        // 更新显示文本
        categoryDisplay.value = getCategoryText(selectedProduct.category)
        deviceTypeDisplay.value = getDeviceTypeName(selectedProduct.deviceTypeId)
      }
    }
    
    // 按类型加载产品
    const loadProductsByCategory = async (category) => {
      try {
        loadingProducts.value = true
        const response = await productApi.getProducts({ category })
        if (response.data) {
          availableProducts.value = response.data.list || response.data || []
        } else {
          console.error('获取产品失败:', response?.message)
          ElMessage.error('加载产品失败，请刷新页面重试')
          availableProducts.value = []
        }
      } catch (error) {
        console.error('加载产品失败:', error)
        ElMessage.error('加载产品失败，请检查网络连接并刷新页面重试')
        availableProducts.value = []
      } finally {
        loadingProducts.value = false
      }
    }
    
    // 按设备类型加载产品
    const loadProductsByDeviceType = async (deviceType) => {
      try {
        loadingProducts.value = true
        const response = await productApi.getProducts({ deviceType })
        if (response.data) {
          availableProducts.value = response.data.list || response.data || []
        } else {
          console.error('获取产品失败:', response?.message)
          ElMessage.error('加载产品失败，请刷新页面重试')
          availableProducts.value = []
        }
      } catch (error) {
        console.error('加载产品失败:', error)
        ElMessage.error('加载产品失败，请检查网络连接并刷新页面重试')
        availableProducts.value = []
      } finally {
        loadingProducts.value = false
      }
    }
    
    
    
    // 重置添加表单
    const resetAddForm = () => {
      addForm.value = {
        deviceCode: '',
        deviceName: '',
        category: '',
        deviceType: '',
        productId: null,
        familyId: null,
        location: '',
        notificationPolicy: notificationPolicies.value.length > 0 ? notificationPolicies.value[0].value : null,
        description: '',
        macAddress: ''
      }
      categoryDisplay.value = ''
      deviceTypeDisplay.value = ''
      availableDeviceTypes.value = []
      availableProducts.value = []
    }
    
    // 提交添加设备
    const submitAddDevice = async () => {
      if (!addFormRef.value) {
        return
      }
      
      try {
        await addFormRef.value.validate()
        
        // 验证设备类型是否已选择
        if (!addForm.value.deviceType) {
          ElMessage.error('请先选择产品型号以确定设备类型')
          return
        }
        
        addingDevice.value = true
        
        const deviceData = {
          deviceCode: addForm.value.deviceCode,
          deviceName: addForm.value.deviceName,
          deviceType: addForm.value.deviceType,
          room: addForm.value.location, // location字段对应数据库的room字段，保存场所ID
          notificationPolicy: addForm.value.notificationPolicy,
          description: addForm.value.description,
          macAddress: addForm.value.macAddress
        }
        
        // 获取当前用户的家庭ID
        const family = localStorage.getItem('family')
        if (!family || family === 'null') {
          ElMessage.error('暂未关联家庭，无法添加设备')
          return
        }
        
        const familyData = JSON.parse(family)
        if (!familyData || !familyData.id) {
          ElMessage.error('家庭信息无效，无法添加设备')
          return
        }
        const familyId = familyData.id
        
        // 将设备添加到当前用户的家庭
        const response = await deviceApi.addDeviceToFamily(familyId, {
          ...deviceData,
          productId: addForm.value.productId
        })
        if (response.data) {
          await loadDevices() // 重新加载设备列表
          showAddDialog.value = false
          resetAddForm()
          ElMessage.success('设备添加成功')
        }
      } catch (error) {
        if (error !== false) { // 非表单验证错误
          console.error('添加设备失败:', error)
          ElMessage.error('添加设备失败')
        }
      } finally {
        addingDevice.value = false
      }
    }
    
    // 获取设备类型名称
    const getDeviceTypeName = (deviceTypeId) => {
      if (!deviceTypeId) return '未设置'
      
      // 如果设备类型列表为空，提示数据加载问题
      if (!deviceTypes.value || deviceTypes.value.length === 0) {
        return '设备类型数据加载失败'
      }
      
      // 首先尝试通过ID在设备类型列表中查找（支持 UUID 字符串 ID）
      const foundType = deviceTypes.value.find(type => type.id === deviceTypeId || type.id === String(deviceTypeId))
      if (foundType) {
        return foundType.name
      }
      
      // 如果通过ID没找到，尝试通过code查找（兼容旧数据）
      const foundByCode = deviceTypes.value.find(type => type.code === deviceTypeId)
      if (foundByCode) {
        return foundByCode.name
      }
      
      // 如果都找不到，说明数据不一致
      return `未知设备类型(ID:${deviceTypeId})`
    }
    
    // CSV导入相关方法
    const downloadTemplate = () => {
      const headers = ['deviceCode', 'deviceName', 'productModel', 'familyName', 'location', 'description', 'macAddress']
      const sampleData = [
        ['SMOKE_001', '客厅烟感器', 'SF-001', '张家大院', '客厅', '客厅主要区域烟雾检测', '00:11:22:33:44:55'],
        ['GAS_001', '厨房燃气传感器', 'GS-002', '张家大院', '厨房', '厨房燃气泄漏检测', '00:11:22:33:44:56']
      ]
      
      const csvContent = [headers.join(','), ...sampleData.map(row => row.join(','))].join('\n')
      const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = '设备导入模板.csv'
      link.click()
      ElMessage.success('模板下载成功')
    }
    
    const beforeUpload = (file) => {
      if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
        ElMessage.error('只能上传CSV文件')
        return false
      }
      
      if (file.size > 5 * 1024 * 1024) {
        ElMessage.error('文件大小不能超过5MB')
        return false
      }
      
      importFile.value = file
      parseCSVFile(file)
      return false
    }
    
    const parseCSVFile = (file) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          const csv = e.target.result
          const lines = csv.split('\n').filter(line => line.trim())
          
          if (lines.length < 2) {
            ElMessage.error('CSV文件格式错误，至少需要包含表头和一行数据')
            return
          }
          
          const headers = lines[0].split(',').map(h => h.trim())
          const expectedHeaders = ['deviceCode', 'deviceName', 'productModel', 'familyName', 'location', 'description', 'macAddress']
          
          if (!expectedHeaders.every(header => headers.includes(header))) {
            ElMessage.error('CSV文件表头格式错误，请检查表头格式')
            return
          }
          
          const data = []
          const errors = []
          
          for (let i = 1; i < lines.length; i++) {
            const values = lines[i].split(',').map(v => v.trim())
            const row = {}
            
            headers.forEach((header, index) => {
              row[header] = values[index] || ''
            })
            
            // 验证必填字段
            if (!row.deviceCode) {
              errors.push({ row: i + 1, field: 'deviceCode', error: '设备编号不能为空' })
            }
            if (!row.deviceName) {
              errors.push({ row: i + 1, field: 'deviceName', error: '设备名称不能为空' })
            }
            if (!row.productModel) {
              errors.push({ row: i + 1, field: 'productModel', error: '产品型号不能为空' })
            }
            if (!row.familyName) {
              errors.push({ row: i + 1, field: 'familyName', error: '家庭名称不能为空' })
            }
            
            data.push(row)
          }
          
          previewData.value = data
          importErrors.value = errors
          
          if (errors.length > 0) {
            ElMessage.warning(`发现 ${errors.length} 个数据验证错误，请修正后重新上传`)
          } else {
            ElMessage.success(`CSV文件解析成功，共 ${data.length} 条数据`)
          }
        } catch (error) {
          ElMessage.error('CSV文件解析失败，请检查文件格式')
        }
      }
      reader.readAsText(file, 'UTF-8')
    }
    
    const removeFile = () => {
      importFile.value = null
      previewData.value = []
      importErrors.value = []
    }
    
    const resetImportForm = () => {
      removeFile()
    }
    
    const submitImport = async () => {
      importing.value = true
      try {
        // 模拟API调用
        await new Promise(resolve => setTimeout(resolve, 2000))
        
        // 这里应该调用后端API进行批量导入
        // const response = await api.post('/api/devices/import', { data: previewData.value })
        
        const importedDevices = previewData.value.map((item, index) => ({
          id: Date.now() + index,
          deviceId: item.deviceCode,
          deviceName: item.deviceName,
          deviceType: { name: '未知', code: 'UNKNOWN' }, // 实际应该根据productModel查找
          status: 1,
          room: item.location,
          description: item.description,
          macAddress: item.macAddress,
          batteryLevel: Math.floor(Math.random() * 100),
          signalStrength: Math.floor(Math.random() * 50) - 80,
          lastHeartbeat: new Date().toISOString(),
          createdAt: new Date().toISOString()
        }))
        
        devices.value.unshift(...importedDevices)
        applySort()
        calculateStats()
        showImportDialog.value = false
        resetImportForm()
        ElMessage.success(`成功导入 ${importedDevices.length} 台设备`)
      } catch (error) {
        ElMessage.error('导入设备失败')
      } finally {
        importing.value = false
      }
    }
    
    // 格式化文件大小
    const formatFileSize = (bytes) => {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    }
    
    // 格式化日期时间
    const formatDateTime = (timestamp) => {
      return new Date(timestamp).toLocaleString('zh-CN')
    }

    // 获取状态类型（用于el-tag的type属性）
    const getStatusType = (status) => {
      // 确保status是数字类型
      const deviceStatus = Number(status)
      console.log('getStatusType - 原始值:', status, '类型:', typeof status, '转换后:', deviceStatus)
      if (deviceStatus === 1) return 'success'
      if (deviceStatus === 0) return 'danger'
      return 'info'
    }

    // 获取状态文本
    const getStatusText = (status) => {
      // 确保status是数字类型
      const deviceStatus = Number(status)
      console.log('getStatusText - 原始值:', status, '类型:', typeof status, '转换后:', deviceStatus)
      if (deviceStatus === 1) return '在线'
      if (deviceStatus === 0) return '离线'
      return '未知'
    }

    return {
      loading,
      selectedDeviceType,
      selectedStatus,
      devices,
      allDevices,
      handleSortChange,
      sortState,
      deviceTypes,
      deviceStats,
      detailDialogVisible,
      selectedDevice,
      filterDevices,
      filterByStatus,
      getStatusType,
      getStatusText,
      // Device addition
      showAddDialog,
      addForm,
      addRules,
      addingDevice,
      addFormRef,
      // Device editing
      editDeviceVisible,
      editDeviceForm,
      editDeviceRules,
      editingDevice,
      editDeviceFormRef,
      editCategoryDisplay,
      editDeviceTypeDisplay,
      availableCategories,
      availableDeviceTypes,
      availableProducts,
      allAvailableProducts,
      availableFamilies,
      familyLocations,
      notificationPolicies,
      enabledNotificationPolicies,
      categoryDisplay,
      deviceTypeDisplay,
      // CSV import
      showImportDialog,
      importFile,
      previewData,
      importErrors,
      importing,
      // Methods
      loadDevices,
      loadFamilyLocations,
      loadNotificationPolicies,
      getLocationName,
      refreshDevices,
      viewDeviceDetail,
      toggleDeviceArmStatus,
      deleteDevice,
      updateDeviceNotificationPolicy,
      formatTime,
      formatDateTime,
      formatFileSize,
      // Device addition methods
      loadAllProducts,
      getCategoryText,
      onCategoryChange,
      onDeviceTypeChange,
      onProductChange,
      submitAddDevice,
      resetAddForm,
      getDeviceTypeName,
      // Device editing methods
      editDevice,
      submitEditDevice,
      resetEditDeviceForm,
      onEditProductChange,
      // CSV import methods
      downloadTemplate,
      beforeUpload,
      parseCSVFile,
      removeFile,
      resetImportForm,
      submitImport,
      // Sorting
      applySort
    }
  }
}
</script>

<style scoped>
.device-management {
  /* 移除padding，因为Layout组件已经提供了 */
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #333;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.device-stats {
  margin-bottom: 20px;
}

.stat-card {
  text-align: center;
  cursor: pointer;
  transition: transform 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-card.active {
  border: 2px solid #409EFF;
  box-shadow: 0 2px 12px rgba(64, 158, 255, 0.3);
}

.stat-item {
  padding: 10px;
}

.stat-value {
  font-size: 24px;
  font-weight: bold;
  color: #409EFF;
  margin-bottom: 5px;
}

.stat-value.online {
  color: #67C23A;
}

.stat-value.offline {
  color: #F56C6C;
}

.stat-value.rate {
  color: #E6A23C;
}

.stat-label {
  font-size: 14px;
  color: #666;
}

.device-list {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.view-toggle {
  margin-left: auto;
}

/* 表格容器样式 */
.table-container {
  width: 100%;
  overflow-x: auto;
}

/* 滚动条宽度适配 */
:deep(.el-scrollbar__view) {
  width: 100% !important;
}

:deep(.el-scrollbar__wrap),
:deep(.el-scrollbar__wrap--hidden-default) {
  width: 100% !important;
}

/* 表格样式优化 */
:deep(.el-table) {
  width: 100% !important;
  min-width: 100%;
}

:deep(.el-table__body) {
  width: 100% !important;
}

:deep(.el-table__header) {
  width: 100% !important;
}

/* 确保表格内容区域宽度适配 */
:deep(.el-table__body-wrapper) {
  width: 100% !important;
}

:deep(.el-table__header-wrapper) {
  width: 100% !important;
}

/* 修复表格固定列的宽度问题 */
:deep(.el-table__fixed-right) {
  right: 0 !important;
}

:deep(.el-table--fit) {
  width: 100% !important;
}

:deep(.el-table td),
:deep(.el-table th) {
  padding: 12px 0;
  border-bottom: 1px solid #ebeef5;
  text-align: center !important;
}

/* 修复单元格内div宽度问题 */
:deep(.el-table .cell) {
  width: 100% !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  text-align: center !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

/* 确保表头单元格也居中 */
:deep(.el-table th .cell) {
  width: 100% !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  text-align: center !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

:deep(.el-table__row) {
  transition: background-color 0.2s;
}

:deep(.el-table__row:hover) {
  background-color: #f5f7fa;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: center;
}

.action-buttons .el-button {
  margin: 0;
  font-size: 12px;
  padding: 4px 10px;
  min-width: 58px;
  white-space: nowrap;
}


/* 心跳时间样式 */
.heartbeat-time {
  font-size: 12px;
  color: #666;
}

/* 无数据样式 */
.no-data {
  color: #c0c4cc;
  font-style: italic;
}

.device-cards {
  margin-top: 20px;
}

.cards-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.device-card {
  cursor: pointer;
  transition: all 0.3s;
}

.device-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}

.device-card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 15px;
}

.device-info h3 {
  margin: 0 0 5px 0;
  color: #333;
  font-size: 16px;
}

.device-id {
  margin: 0;
  color: #666;
  font-size: 12px;
}

.device-card-body {
  space-y: 10px;
}

.device-type,
.device-product,
.device-family,
.device-location {
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
}

.label {
  font-weight: 500;
  color: #666;
}


.device-metrics {
  display: flex;
  justify-content: space-between;
  margin-top: 10px;
  font-size: 12px;
}

.metric {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.metric-label {
  color: #666;
  margin-bottom: 2px;
}

.metric-value {
  color: #333;
  font-weight: 500;
}

.device-detail {
  margin-top: 20px;
}

/* 响应式样式 */
@media (max-width: 1200px) {
  .action-buttons {
    flex-direction: column;
    gap: 4px;
  }
  
  .action-buttons .el-button {
    min-width: 60px;
    font-size: 11px;
  }
}

@media (max-width: 768px) {
  .table-container {
    overflow-x: scroll;
  }
  
  :deep(.el-table td),
  :deep(.el-table th) {
    padding: 8px 4px;
  }
  
  .action-buttons .el-button {
    padding: 4px 6px;
    font-size: 10px;
  }
  
}

/* 表格滚动条样式 */
.table-container::-webkit-scrollbar {
  height: 6px;
}

.table-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.table-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* 对话框样式 */
.dialog-footer {
  text-align: right;
}

/* 添加设备表单样式 */
.form-tip {
  font-size: 12px;
  color: #909399;
  margin-top: 4px;
  line-height: 1.4;
}

/* CSV导入样式 */
.import-section {
  max-height: 60vh;
  overflow-y: auto;
}

.import-instructions ul {
  margin: 8px 0;
  padding-left: 20px;
}

.import-instructions li {
  margin: 4px 0;
  line-height: 1.4;
}

.import-actions {
  margin: 20px 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.file-info {
  margin: 20px 0;
}

.file-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.file-details p {
  margin: 5px 0;
  color: #666;
  font-size: 14px;
}

.preview-section {
  margin: 20px 0;
}

.preview-section h4 {
  margin: 0 0 10px 0;
  color: #333;
  font-size: 16px;
}

.preview-summary {
  margin: 10px 0 0 0;
  color: #666;
  font-size: 14px;
  text-align: center;
}

.error-section {
  margin: 20px 0;
}

.error-section h4 {
  margin: 0 0 10px 0;
  color: #f56c6c;
  font-size: 16px;
}

/* 产品信息样式 */
.product-info {
  text-align: center;
}

.product-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 2px;
}

.product-model {
  font-size: 12px;
  color: #666;
}
</style>